import React, { useState } from 'react';
import { bugReportsApi, type BugReportSeverity } from '../api/services';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Bug-report / support dialog. Triggered by the Support button in the app shell.
// Submits to /api/bug-reports where PlatformBugReportsPage picks it up. Captures
// page URL + UA automatically so the user doesn't have to.

export default function SupportDialog({ open, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugReportSeverity>('Medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('Medium');
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('A short title and a description are both required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await bugReportsApi.submit({
        title: title.trim(),
        description: description.trim(),
        severity,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });
      setSuccess(true);
      setTimeout(() => {
        reset();
        onClose();
      }, 1800);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not send your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Describe what went wrong and what you were trying to do. The platform team
          gets these directly. We'll capture the page URL and browser automatically —
          you don't need to.
        </p>

        {success && (
          <Alert variant="success">
            <AlertDescription>
              Thanks — your report is in. The platform team will review it and reach
              out if they need more from you.
            </AlertDescription>
          </Alert>
        )}
        {error && !success && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sd-title">Short title</Label>
            <Input
              id="sd-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Calendar sync crashed when I clicked Replan"
              disabled={submitting || success}
              autoFocus
              maxLength={200}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sd-desc">What happened?</Label>
            <Textarea
              id="sd-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Steps to reproduce, what you expected, what actually happened, anything else that might help."
              rows={5}
              maxLength={4000}
              disabled={submitting || success}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>How serious is this?</Label>
            <Select
              value={severity}
              onValueChange={(v) => setSeverity(v as BugReportSeverity)}
              disabled={submitting || success}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">Low — small annoyance, no rush</SelectItem>
                <SelectItem value="Medium">Medium — affects how I work but I have a workaround</SelectItem>
                <SelectItem value="High">High — blocking me from getting today's work done</SelectItem>
                <SelectItem value="Critical">Critical — losing data or affecting my whole team</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md bg-muted p-2.5">
            <p className="block text-xs text-muted-foreground">
              We'll attach automatically: <code>{window.location.pathname}</code> and your browser version.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || success || !title.trim() || !description.trim()}
          >
            {submitting ? 'Sending…' : 'Send report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
